import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { applyStoredConsent, saveConsent, readConsent, OPEN_PREFS_EVENT } from "@/lib/cookieConsent";

const Row = ({ title, copy, checked, onCheckedChange, disabled, testId }) => (
  <div className="flex items-center justify-between gap-4 rounded-xl bg-secondary/60 px-4 py-3">
    <div className="min-w-0">
      <div className="text-sm font-[620] text-foreground">{title}</div>
      <div className="text-xs leading-5 text-muted-foreground">{copy}</div>
    </div>
    <Switch checked={checked} onCheckedChange={onCheckedChange} disabled={disabled} aria-label={title} data-testid={testId} />
  </div>
);

/** Slim opt-in cookie bar. The detail lives in the preferences dialog, not the bar. */
export const CookieBanner = () => {
  const [showBar, setShowBar] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [analytics, setAnalytics] = useState(false);

  useEffect(() => {
    if (!applyStoredConsent()) setShowBar(true);
    const reopen = () => {
      setAnalytics(!!readConsent()?.analytics);
      setShowPrefs(true);
    };
    window.addEventListener(OPEN_PREFS_EVENT, reopen);
    return () => window.removeEventListener(OPEN_PREFS_EVENT, reopen);
  }, []);

  const decide = (value) => {
    saveConsent(value);
    setShowBar(false);
    setShowPrefs(false);
  };

  return (
    <>
      {showBar && (
        <div className="fixed inset-x-0 bottom-0 z-[70] border-t border-border bg-card/95 backdrop-blur-md" data-testid="cookie-banner">
          <div className="mx-auto flex max-w-7xl flex-col gap-2 px-4 py-2.5 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:px-6 lg:px-8">
            <p className="text-xs leading-5 text-muted-foreground sm:text-sm">
              <span className="sm:hidden">Cookies keep you signed in. Analytics only if you allow it.</span>
              <span className="hidden sm:inline">We use cookies to keep you signed in and, with your permission, to measure how the site is used.</span>
            </p>
            <div className="flex shrink-0 items-center gap-1.5">
              <Button size="sm" className="h-8 rounded-full px-4 text-xs" onClick={() => decide(true)} data-testid="cookie-accept-all">Accept</Button>
              <Button size="sm" variant="outline" className="h-8 rounded-full px-4 text-xs" onClick={() => decide(false)} data-testid="cookie-reject-all">Reject</Button>
              <Button size="sm" variant="ghost" className="h-8 rounded-full px-3 text-xs" onClick={() => { setAnalytics(false); setShowPrefs(true); }} data-testid="cookie-customize">Choices</Button>
              <button
                type="button"
                onClick={() => decide(false)}
                className="ml-0.5 rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                aria-label="Close and reject analytics cookies"
                data-testid="cookie-dismiss"
              >
                <X className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      )}

      <Dialog open={showPrefs} onOpenChange={setShowPrefs}>
        <DialogContent className="sm:max-w-lg" data-testid="cookie-banner-options">
          <DialogHeader>
            <DialogTitle>Cookie preferences</DialogTitle>
            <DialogDescription className="text-left leading-6">
              Startup Otter uses one strictly necessary cookie to keep you signed in and secure. With your permission we
              also use Google Analytics to understand, in aggregate, how the platform is used so we can improve it. We
              never run advertising networks or advertising cookies, and we do not sell your information. You can change
              this any time from Cookie Preferences in the footer — see our{" "}
              <Link to="/privacy" className="font-[560] text-primary hover:underline" data-testid="cookie-banner-privacy-link">Privacy Policy</Link>.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <Row title="Strictly necessary" copy="Keeps you signed in and secure. Always on." checked disabled testId="cookie-toggle-essential" />
            <Row
              title="Analytics"
              copy="Google Analytics, aggregate usage only. Off unless you allow it."
              checked={analytics}
              onCheckedChange={setAnalytics}
              testId="cookie-toggle-analytics"
            />
          </div>

          <DialogFooter>
            <Button onClick={() => decide(analytics)} className="rounded-full px-6" data-testid="cookie-save-choices">Save my choices</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
